import "./style.css";
import type { DailyEntry, DailySchedule, DeepSkyEntry, DeepSkySchedule } from "../scripts/lib/types.ts";
import { GameController } from "./game/game.ts";
import { GameAudio } from "./audio/index.ts";
import { dateForPuzzleDay, recordPuzzleSolved, recordPuzzleStarted } from "./analytics.ts";
import { DaySelect } from "./screens/daySelect.ts";
import { markDayCompleted, setCurrentDay } from "./storage.ts";
import { reducedMotion } from "./motion.ts";
import { todayDayIndex } from "./daily.ts";
import { asset } from "./paths.ts";
import { buildShareText } from "./share.ts";
import { SITE_URL } from "../scripts/lib/site.ts";
import { ICON_BACK, ICON_RESTART, ICON_SHARE, ICON_SOUND_OFF, ICON_SOUND_ON, ICON_UNDO } from "./icons.ts";

const selectScreen = document.querySelector<HTMLElement>("#selectScreen");
const gameScreen = document.querySelector<HTMLElement>("#gameScreen");
const dayGrid = document.querySelector<HTMLElement>("#dayGrid");
const selectMuteBtn = document.querySelector<HTMLButtonElement>("#selectMute");
const selectMuteIcon = document.querySelector<HTMLElement>("#selectMuteIcon");

const canvas = document.querySelector<HTMLCanvasElement>("#board");
const boardWrap = document.querySelector<HTMLElement>("#boardWrap");
const progressEl = document.querySelector<HTMLElement>("#progress");
const dayLabel = document.querySelector<HTMLElement>("#dayLabel");
const backBtn = document.querySelector<HTMLButtonElement>("#back");
const backIcon = document.querySelector<HTMLElement>("#backIcon");
const undoBtn = document.querySelector<HTMLButtonElement>("#undo");
const undoIcon = document.querySelector<HTMLElement>("#undoIcon");
const restartBtn = document.querySelector<HTMLButtonElement>("#restart");
const restartIcon = document.querySelector<HTMLElement>("#restartIcon");
const muteBtn = document.querySelector<HTMLButtonElement>("#mute");
const muteIcon = document.querySelector<HTMLElement>("#muteIcon");
const winBanner = document.querySelector<HTMLElement>("#winBanner");
const winMenuBtn = document.querySelector<HTMLButtonElement>("#winMenu");
const winShareBtn = document.querySelector<HTMLButtonElement>("#winShare");
const winShareIcon = document.querySelector<HTMLElement>("#winShareIcon");
const winShareLabel = document.querySelector<HTMLElement>("#winShareLabel");
const winNextBtn = document.querySelector<HTMLButtonElement>("#winNext");
const winPeekBtn = document.querySelector<HTMLButtonElement>("#winPeek");
const winDestination = document.querySelector<HTMLElement>("#winDestination");
const winDestinationName = document.querySelector<HTMLElement>("#winDestinationName");
const winDestinationFact = document.querySelector<HTMLElement>("#winDestinationFact");
const winDestinationCredit = document.querySelector<HTMLElement>("#winDestinationCredit");

if (
  !selectScreen ||
  !gameScreen ||
  !dayGrid ||
  !selectMuteBtn ||
  !selectMuteIcon ||
  !canvas ||
  !boardWrap ||
  !progressEl ||
  !dayLabel ||
  !backBtn ||
  !backIcon ||
  !undoBtn ||
  !undoIcon ||
  !restartBtn ||
  !restartIcon ||
  !muteBtn ||
  !muteIcon ||
  !winBanner ||
  !winMenuBtn ||
  !winShareBtn ||
  !winShareIcon ||
  !winShareLabel ||
  !winNextBtn ||
  !winPeekBtn ||
  !winDestination ||
  !winDestinationName ||
  !winDestinationFact ||
  !winDestinationCredit
) {
  throw new Error("expected page structure missing from index.html");
}

backIcon.innerHTML = ICON_BACK;
undoIcon.innerHTML = ICON_UNDO;
restartIcon.innerHTML = ICON_RESTART;
winShareIcon.innerHTML = ICON_SHARE;

const audio = new GameAudio();

const game = new GameController(
  canvas,
  boardWrap,
  {
    onProgress(filled, total) {
      pathFilled = filled;
      progressEl.textContent = `${filled} / ${total}`;
    },
    onWin(won) {
      if (won && current) {
        recordPuzzleSolved(dateForPuzzleDay(current.day), (performance.now() - startedAt) / 1000);
        markDayCompleted(current.day);
        daySelect.refresh();
        lastShareText = buildShareText(current.day, performance.now() - startedAt, current.level, SITE_URL);

        const deepSky = deepSkyMap?.get(current.day) ?? null;
        winDestination.hidden = !deepSky;
        if (deepSky) {
          winDestinationName.textContent = deepSky.name;
          winDestinationFact.textContent = deepSky.fact;
          winDestinationCredit.textContent = deepSky.credit;
        }
      }
      hasWon = won;
      showResults(won);
    },
  },
  audio,
);

let schedule: DailySchedule | null = null;
let deepSkyMap: Map<number, DeepSkyEntry> | null = null;
let current: DailyEntry | null = null;
/** Today's day index, clamped to the schedule — the unlock boundary in the day list. */
let todayDay = 1;
/** Set when a level actually appears on screen (see loadDay) — the share text's clock. */
let startedAt = 0;
let lastShareText = "";

const daySelect = new DaySelect(dayGrid, {
  onSelect(entry) {
    loadDay(entry);
    showScreen("game");
  },
});

/** Whether the current level has been solved — gates the show/hide affordance. */
let hasWon = false;
/** Tiles on the path right now, mirrored from onProgress — see rolloverIfNewDay. */
let pathFilled = 0;

/**
 * Show or hide the results sheet.
 *
 * The sheet overlays the bottom of the photograph rather than pushing the board
 * up out of its way, and that is a deliberate reversal of an earlier attempt.
 * Reserving the sheet's height did guarantee the photo was never covered — but
 * it cost the photo roughly a fifth of its width to do it, and a smaller
 * picture is a worse reveal than a partly covered one. The photo is the payoff;
 * it stays as large as the frame allows.
 *
 * What makes the overlay acceptable is that it is not permanent: tapping the
 * board dismisses the sheet, a chip brings it back, and the sheet is capped at
 * half the board area (see .win-banner) so there is always something to look
 * at. The real fix for "I cannot see the picture" was a taller frame, not a
 * smaller board.
 */
function showResults(show: boolean): void {
  winBanner!.classList.toggle("win-banner-visible", show);
  winPeekBtn!.hidden = show || !hasWon;
  boardWrap!.classList.toggle("board-wrap-results", show);
}

/**
 * The day after the one being played, or null when there isn't one.
 *
 * On today's puzzle that is always null -- tomorrow's is not out yet -- which
 * is why the button it controls stays hidden for almost every player. It earns
 * its place only when someone opens an older day from the list and can
 * genuinely move forward through the archive.
 */
function nextDay(): DailyEntry | null {
  if (!schedule || !current) return null;
  if (current.day + 1 > todayDay) return null;
  return schedule.days[current.day] ?? null; // days[] is 0-based, so index day is day+1
}

function showScreen(name: "select" | "game"): void {
  selectScreen!.classList.toggle("screen-hidden", name !== "select");
  gameScreen!.classList.toggle("screen-hidden", name !== "game");
  if (name === "select") daySelect.refresh();
  else game.resize();
}

/** Loads a day into the running game, with a short crossfade unless reduced-motion. */
function loadDay(entry: DailyEntry): void {
  current = entry;
  setCurrentDay(entry.day);
  dayLabel!.textContent = `Day ${entry.day}`;
  hasWon = false;
  // Decided per day rather than at win time: current has just changed, and a
  // button that appears only sometimes must be resolved before it can be seen.
  winNextBtn!.hidden = nextDay() === null;
  winBanner!.classList.remove("win-banner-visible");
  winPeekBtn!.hidden = true;
  boardWrap!.classList.remove("board-wrap-results");

  const deepSky = deepSkyMap?.get(entry.day) ?? null;

  if (reducedMotion() || boardWrap!.classList.contains("board-wrap-fading")) {
    game.loadLevel(entry.level, deepSky);
    startedAt = performance.now();
    return;
  }

  boardWrap!.classList.add("board-wrap-fading");
  window.setTimeout(() => {
    game.loadLevel(entry.level, deepSky);
    startedAt = performance.now();
    boardWrap!.classList.remove("board-wrap-fading");
  }, 180);
}

/**
 * Roll the board over to the new puzzle when the local date changes.
 *
 * `todayDayIndex()` is only read once, at boot, which is right for a page that
 * gets opened and closed — and wrong for the two ways this app is actually
 * left running: an <iframe> on a page someone keeps open, and a phone that
 * suspends the tab overnight and restores it in the morning. Both would still
 * be showing yesterday.
 *
 * Advancing is deliberately conservative. The day list unlocks immediately,
 * but the board is only swapped when there is nothing to lose — no path drawn,
 * or the puzzle already solved. Pulling a half-drawn path out from under
 * someone at midnight would be a worse bug than the one this fixes. `?day=N`
 * opts out entirely; it is an explicit request for one specific day.
 */
function rolloverIfNewDay(): void {
  if (!schedule || requestedDay() !== null) return;

  const fresh = Math.min(Math.max(todayDayIndex(), 1), schedule.days.length);
  if (fresh === todayDay) return;

  todayDay = fresh;
  daySelect.setDays(schedule.days, todayDay);

  if (!current || current.day === todayDay) return;
  if (!hasWon && pathFilled > 1) return; // mid-solve — leave them to it

  const entry = schedule.days.find((d) => d.day === todayDay);
  if (entry) loadDay(entry);
}

/**
 * Fire just after the next local midnight, then re-arm. Timers in a hidden tab
 * are throttled to the point of uselessness, which is exactly why this is a
 * belt to the visibilitychange braces rather than the only mechanism.
 */
function scheduleMidnightCheck(): void {
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 2);
  window.setTimeout(() => {
    rolloverIfNewDay();
    scheduleMidnightCheck();
  }, Math.max(1000, midnight.getTime() - now.getTime()));
}

// Browsers keep an AudioContext suspended until a user gesture. Listening on
// the window in the capture phase means this runs before the game's own
// canvas handler on the very first pointerdown — so the note for that first
// tile is not the one that gets swallowed — and it covers a button tap as
// well as a board tap. Pointer events give mouse, touch and pen for free, so
// there is no device branching here (see input.ts).
//
// Not `once`: iOS suspends the context again when the app is backgrounded,
// and the next tap is exactly when we want it back.
window.addEventListener(
  "pointerdown",
  (event) => {
    audio.unlock();
    if (current && event.target instanceof Node && boardWrap!.contains(event.target)) {
      recordPuzzleStarted(dateForPuzzleDay(current.day));
    }
  },
  { capture: true },
);

backBtn.addEventListener("click", () => showScreen("select"));
undoBtn.addEventListener("click", () => game.undo());
restartBtn.addEventListener("click", () => game.restart());
winMenuBtn.addEventListener("click", () => showScreen("select"));

/**
 * Tapping the board toggles the results sheet once a level is solved, so the
 * photograph can be seen without it. Bound to the wrapper rather than the
 * canvas so the surrounding letterboxing counts too, and a click that started
 * inside the sheet is ignored — otherwise pressing Share would dismiss it.
 */
boardWrap.addEventListener("click", (event) => {
  if (!hasWon) return;
  if (event.target instanceof Node && winBanner.contains(event.target)) return;
  showResults(!winBanner.classList.contains("win-banner-visible"));
});

winPeekBtn.addEventListener("click", (event) => {
  event.stopPropagation();
  showResults(true);
});

/** navigator.clipboard needs a secure context; the textarea trick works everywhere else. */
async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

let shareFlashTimer: number | undefined;
winShareBtn.addEventListener("click", async () => {
  try {
    await copyToClipboard(lastShareText);
    window.clearTimeout(shareFlashTimer);
    winShareLabel.textContent = "Copied!";
    shareFlashTimer = window.setTimeout(() => {
      winShareLabel.textContent = "Share";
    }, 1500);
  } catch (err) {
    console.error("copy failed", err);
  }
});

winNextBtn.addEventListener("click", () => {
  const next = nextDay();
  // Guarded by the hidden attribute, but a stale click must not dead-end.
  if (next) loadDay(next);
  else showScreen("select");
});

// An arrow const rather than a hoisted `function`: TypeScript only carries
// the null-narrowing above into closures created after it.
const syncMuteButtons = (): void => {
  const muted = audio.muted;
  const icon = muted ? ICON_SOUND_OFF : ICON_SOUND_ON;
  const label = muted ? "Unmute sound" : "Mute sound";
  for (const btn of [muteBtn, selectMuteBtn]) {
    btn.setAttribute("aria-pressed", String(muted));
    btn.setAttribute("aria-label", label);
  }
  muteIcon.innerHTML = icon;
  selectMuteIcon.innerHTML = icon;
};

muteBtn.addEventListener("click", () => {
  audio.toggleMuted();
  syncMuteButtons();
});
selectMuteBtn.addEventListener("click", () => {
  audio.toggleMuted();
  syncMuteButtons();
});

syncMuteButtons();

// One resize path for every trigger: window resize, orientation change, and
// the container's own box changing (address bar show/hide, split view).
// Debounced via setTimeout rather than requestAnimationFrame — rAF is
// throttled on a backgrounded/hidden page, which would otherwise delay the
// very resize that's supposed to fire when the app regains focus.
let resizeQueued = false;
function queueResize(): void {
  if (resizeQueued) return;
  resizeQueued = true;
  setTimeout(() => {
    resizeQueued = false;
    game.resize();
  }, 0);
}

window.addEventListener("resize", queueResize);
window.addEventListener("orientationchange", queueResize);
document.addEventListener("visibilitychange", () => {
  if (document.hidden) return;
  queueResize();
  rolloverIfNewDay();
});
// A desktop tab can regain focus without ever having been "hidden" — window
// focus catches the case visibilitychange misses.
window.addEventListener("focus", rolloverIfNewDay);
new ResizeObserver(queueResize).observe(boardWrap);

/** `?day=200` jumps straight into that day — how a specific day gets played and tested. */
function requestedDay(): number | null {
  const raw = new URLSearchParams(window.location.search).get("day");
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 ? n : null;
}

/**
 * The reveal catalogue is optional — a day with no entry just plays without
 * one (see Renderer: no photo, straight win banner). That's what lets this
 * roll out incrementally instead of blocking the whole schedule on every day
 * having curated art.
 */
async function loadDeepSky(): Promise<Map<number, DeepSkyEntry> | null> {
  try {
    const res = await fetch(asset("deepsky.json"));
    if (!res.ok) return null;
    const loaded: DeepSkySchedule = await res.json();
    return new Map(loaded.days.map((entry) => [entry.day, entry]));
  } catch (err) {
    console.warn("deep-sky catalogue failed to load; playing without it", err);
    return null;
  }
}

async function main(): Promise<void> {
  const [res, deepSky] = await Promise.all([fetch(asset("daily.json")), loadDeepSky()]);
  const loaded: DailySchedule = await res.json();
  schedule = loaded;
  deepSkyMap = deepSky;
  todayDay = Math.min(Math.max(todayDayIndex(), 1), loaded.days.length);
  daySelect.setDays(loaded.days, todayDay);

  // ?day=N is an unrestricted dev/testing override — it ignores the lock.
  const wanted = requestedDay();
  if (wanted !== null) {
    const entry = loaded.days.find((d) => d.day === wanted);
    if (entry) {
      loadDay(entry);
      showScreen("game");
      return;
    }
  }

  // Otherwise this is a daily puzzle: open straight into today's day, same
  // as every other player's app on the same local date.
  const entry = loaded.days.find((d) => d.day === todayDay) ?? loaded.days[loaded.days.length - 1];
  loadDay(entry);
  showScreen("game");
  scheduleMidnightCheck();
}

main().catch((err) => {
  console.error(err);
  progressEl.textContent = "Failed to load schedule";
});
